import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import PaceReminderToggle from './PaceReminderToggle';
import {
  Clock, CheckCircle2, CalendarClock, FileWarning, ClipboardList, FileText,
  ArrowRight, Lock, TrendingUp, Compass, ExternalLink, Activity,
} from 'lucide-react';

const PortalOverview = ({ onGoTab }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/internship-portal/dashboard`);
      setData(res.data);
    } catch (e) { setData(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="py-16 text-center text-gray-500" data-testid="overview-loading">Loading your dashboard…</div>;
  if (!data) return <div className="py-16 text-center text-gray-500">Could not load dashboard.</div>;

  const s = data.summary || {};
  const t = data.tracker || {};
  const pw = data.pathway;
  const req = data.requirement;

  return (
    <div className="space-y-6" data-testid="portal-overview">
      {/* Progress hero */}
      <div className="bg-primary rounded-2xl p-6 sm:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-secondary text-sm font-semibold uppercase tracking-wide">Current Placement</p>
            <h3 className="font-serif text-2xl font-bold mt-1">{t.placement_title || 'No placement set yet'}</h3>
            <p className="text-white/80 text-sm mt-1">{t.agency_name || 'Add your placement details to start tracking hours.'}</p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold text-secondary" data-testid="overview-percent">{s.percent_complete || 0}%</p>
            <p className="text-white/70 text-sm">complete</p>
          </div>
        </div>
        <div className="mt-5">
          <Progress value={s.percent_complete || 0} className="h-3 bg-white/20" />
          <div className="flex flex-wrap justify-between text-sm text-white/80 mt-2 gap-2">
            <span>{s.completed_hours || 0} completed</span>
            <span>{s.remaining_hours || 0} remaining</span>
            <span>{s.required_hours || 0} required</span>
          </div>
        </div>
        {(s.supervision_required > 0 || s.direct_client_required > 0 || s.min_months > 0) && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="overview-req-chips">
            {s.supervision_required > 0 && <Chip label="Supervision" value={`${s.supervision_hours || 0} / ${s.supervision_required}`} />}
            {s.direct_client_required > 0 && <Chip label="Direct client" value={`${s.direct_client_hours || 0} / ${s.direct_client_required}`} />}
            {s.min_months > 0 && <Chip label="Months" value={`${s.months_elapsed || 0} / ${s.min_months}`} />}
          </div>
        )}
        {s.expected_completion && (
          <p className="text-white/70 text-sm mt-3 flex items-center gap-1"><TrendingUp className="w-4 h-4 text-secondary" /> Expected completion: <strong className="text-white">{s.expected_completion}</strong></p>
        )}
      </div>

      {/* Pace Coach */}
      <PaceCoach s={s} onGoTab={onGoTab} />
      <PaceReminderToggle />

      {/* Pathway summary card */}
      {data.needs_pathway_setup ? (
        <button onClick={() => onGoTab('pathway')} className="w-full text-left bg-white rounded-2xl border border-secondary/40 p-6 hover:shadow-md transition-all" data-testid="overview-pathway-setup">
          <h4 className="font-serif text-lg font-bold text-primary flex items-center gap-2 mb-1"><Compass className="w-5 h-5 text-secondary" /> Complete Your Pathway</h4>
          <p className="text-sm text-gray-600">Choose Academic field placement or Post-Graduate supervised experience and we'll load your official hour requirements automatically.</p>
          <span className="text-secondary text-sm font-medium inline-flex items-center gap-1 mt-2">Set up now <ArrowRight className="w-4 h-4" /></span>
        </button>
      ) : pw && (
        <div className="bg-white rounded-2xl border p-6" data-testid="overview-pathway-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="font-serif text-lg font-bold text-primary flex items-center gap-2"><Compass className="w-5 h-5 text-secondary" /> My Pathway</h4>
              <p className="text-sm text-gray-700 mt-2">
                <span className="font-semibold">{pw.target_license_name || pw.target_license_code}</span>
                {pw.state ? ` · ${pw.state}` : ' · National (CSWE)'}
                {pw.pathway_type === 'academic' ? ' · Academic Field Placement' : ' · Post-Graduate Supervised Experience'}
              </p>
              {pw.school_name && <p className="text-sm text-gray-500 mt-1">{pw.school_name}</p>}
              {req?.official_source_url && (
                <a href={req.official_source_url} target="_blank" rel="noopener noreferrer" className="text-secondary text-sm font-medium inline-flex items-center gap-1 mt-2">
                  {req.official_source_name || 'Official requirements'} <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            <Button size="sm" variant="outline" className="border-primary text-primary" onClick={() => onGoTab('pathway')} data-testid="overview-edit-pathway">Edit</Button>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Clock} label="Hours Completed" value={s.completed_hours || 0} onClick={() => onGoTab('hours')} />
        <Stat icon={ClipboardList} label="Applications" value={data.counts?.applications || 0} onClick={() => onGoTab('applications')} />
        <Stat icon={CheckCircle2} label="Weekly Logs" value={data.counts?.logs || 0} onClick={() => onGoTab('hours')} />
        <Stat icon={FileText} label="Documents" value={data.counts?.documents || 0} onClick={() => onGoTab('documents')} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming deadlines */}
        <div className="bg-white rounded-2xl border p-6">
          <h4 className="font-serif text-lg font-bold text-primary mb-4 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-secondary" /> Upcoming Deadlines</h4>
          {(data.upcoming_deadlines || []).length === 0 ? (
            <p className="text-gray-500 text-sm">No upcoming deadlines. Add application deadlines or an internship end date to see reminders.</p>
          ) : (
            <ul className="space-y-3">
              {data.upcoming_deadlines.map((d, i) => (
                <li key={i} className="flex items-center justify-between text-sm" data-testid="overview-deadline">
                  <span className="text-gray-700">{d.label}</span>
                  <span className="font-semibold text-primary">{d.date}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Missing documents */}
        <div className="bg-white rounded-2xl border p-6">
          <h4 className="font-serif text-lg font-bold text-primary mb-4 flex items-center gap-2"><FileWarning className="w-5 h-5 text-secondary" /> Missing Documents</h4>
          {(data.missing_documents || []).length === 0 ? (
            <p className="text-green-700 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> All required documents uploaded.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                {data.missing_documents.map((m) => (
                  <span key={m} className="px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded-full text-xs font-medium" data-testid="overview-missing-doc">{m}</span>
                ))}
              </div>
              <Button size="sm" variant="outline" className="border-primary text-primary" onClick={() => onGoTab('documents')}>Upload documents <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </>
          )}
        </div>
      </div>

      {/* Recent logs */}
      <div className="bg-white rounded-2xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-serif text-lg font-bold text-primary flex items-center gap-2"><Clock className="w-5 h-5 text-secondary" /> Recent Weekly Logs</h4>
          <Button size="sm" variant="ghost" className="text-primary" onClick={() => onGoTab('hours')}>View all</Button>
        </div>
        {(data.recent_logs || []).length === 0 ? (
          <p className="text-gray-500 text-sm">No logs yet. Head to <button className="text-secondary underline" onClick={() => onGoTab('hours')}>Hours &amp; Logs</button> to add your first entry.</p>
        ) : (
          <ul className="divide-y">
            {data.recent_logs.map((l) => (
              <li key={l.log_id} className="py-3 flex items-center justify-between text-sm">
                <span className="text-gray-700">{l.date_worked || l.week_start || '—'} · {l.activities || 'Activity'}</span>
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-primary">{l.total_hours}h</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{l.status}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">Your internship records are private. They may be viewed only by you and authorized INeedASocialWorker.com administrators. Schools, agencies, field instructors, and employers cannot access them.</p>
      </div>
    </div>
  );
};

const PaceCoach = ({ s, onGoTab }) => {
  const status = s.pace_status;
  // Not enough info to compute a pace yet
  if (!status) {
    return (
      <button onClick={() => onGoTab('hours')} className="w-full text-left bg-white rounded-2xl border p-5 hover:shadow-md transition-all" data-testid="pace-coach-setup">
        <h4 className="font-serif text-lg font-bold text-primary flex items-center gap-2"><Activity className="w-5 h-5 text-secondary" /> Pace Coach</h4>
        <p className="text-sm text-gray-600 mt-1">Set a weekly hour target and a start date in <strong>Hours &amp; Logs</strong> and I'll track whether you're on pace to finish on time.</p>
      </button>
    );
  }
  const delta = Math.abs(s.pace_delta || 0);
  const weekly = s.weekly_hours || 0;
  const cfg = {
    ahead: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', label: 'Ahead of pace',
      msg: `You're about ${delta}h ahead of schedule. Great momentum — keep it up!` },
    on_track: { bg: 'bg-primary/5', border: 'border-primary/15', text: 'text-primary', label: 'On track',
      msg: `You're right on pace at ${weekly}h/week. Stay consistent and you'll finish on time.` },
    behind: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', label: 'Behind pace',
      msg: `You're about ${delta}h behind where you'd planned to be. Logging a few extra hours this week will get you back on track.` },
  }[status];
  return (
    <div className={`rounded-2xl border p-5 ${cfg.bg} ${cfg.border}`} data-testid="pace-coach">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h4 className="font-serif text-lg font-bold text-primary flex items-center gap-2"><Activity className="w-5 h-5 text-secondary" /> Pace Coach</h4>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full bg-white ${cfg.text}`} data-testid="pace-status-badge">{cfg.label}</span>
      </div>
      <p className={`text-sm ${cfg.text}`} data-testid="pace-message">{cfg.msg}</p>
      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-gray-600">
        <span>Logged so far: <strong className="text-primary">{s.completed_hours || 0}h</strong></span>
        <span>Expected by now: <strong className="text-primary">{s.expected_by_now ?? '—'}h</strong></span>
        {s.expected_completion && <span>Projected finish: <strong className="text-primary">{s.expected_completion}</strong></span>}
      </div>
    </div>
  );
};

const Chip = ({ label, value }) => (  <span className="bg-white/10 rounded-full px-3 py-1 text-xs text-white/90" data-testid="overview-req-chip">
    <span className="text-white/60">{label}:</span> <strong className="text-white">{value}</strong>
  </span>
);

const Stat = ({ icon: Icon, label, value, onClick }) => (  <button onClick={onClick} className="bg-white rounded-2xl border p-5 text-left hover:shadow-md hover:border-secondary/40 transition-all" data-testid={`overview-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-primary" /></div>
    <p className="text-2xl font-bold text-primary">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </button>
);

export default PortalOverview;
